import type { LoaderFunctionArgs, SerializeFrom } from '@remix-run/node';
import { redirect } from '@remix-run/node';
import { json } from '@remix-run/node';
import { getDirectoryPageMeta } from '@modules/category/api';
import { useLoaderData } from '@remix-run/react';
import PageLayout from '~/modules/design-board/component/page-layout/PageLayout';
import {
    PIN_TYPE_PARAM_NAME,
    PIN_TILE_LIMIT_PER_PAGE as defaultPageSize,
} from '~/modules/design-board/config/design-board-config';
import AllPins from '~/modules/design-board/component/all-pins/AllPins';
import { mapMemberDetailedPinsData } from '~/modules/design-board/util/map-member-detailed-pins-data.server';
import type {
    DesignBoardPinTargetType,
    MemberBoardPinCountQuery,
    MemberBoardDetailedPinsQuery,
    DetailedPinFragment,
} from 'generated/graphql';
import {
    MemberBoardPinCountDocument,
    MemberBoardDetailedPinsDocument,
} from 'generated/graphql';
import { mapMemberPinCountData } from '~/modules/design-board/util/map-member-pin-count-data.server';
import { getPaginationOpts } from '~/modules/root/util/get-pagination-opts.server';
import { assertResponse } from '~/modules/root/graphql/assertResponse.server';
import { getValidEdgeNodes } from '~/modules/design-board/util';
import { pluralize } from '~/utils';

import {
    getCanonicalHreflang,
    createMetaTitle,
    createMetaDescription,
    createOGTags,
    mergeMeta,
} from '~/modules/seo';
import type { APHandle } from '~/modules/root';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const title = createMetaTitle(props, [`My Pins | Design Boards`]);

        const description = createMetaDescription([
            `User Design Board description`,
        ]);

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
            }),
            ...getCanonicalHreflang(props),
        ];
    }
);

export const handle: APHandle = {
    isAllPinsPage: true,
};

export const loader = async (args: LoaderFunctionArgs) => {
    const { request, context, params } = args;
    const { device } = context;
    const url = new URL(request.url);
    const isMobile = context.appDisplayMode === 'mobile';

    if (isMobile) {
        return redirect('/member/design-boards', 302);
    }

    const projectUrlSegment = params['projectUrlSegment'];
    const targetType = url.searchParams.get(
        PIN_TYPE_PARAM_NAME
    ) as DesignBoardPinTargetType;

    const pageSettings = getPaginationOpts({
        request,
        device,
        defaultPageSize,
    });

    const [directoryPageMeta, allPins, pinCounts] = await Promise.all([
        getDirectoryPageMeta({
            url,
            ...context,
        }),
        context.client.query<MemberBoardDetailedPinsQuery>({
            query: MemberBoardDetailedPinsDocument,
            variables: {
                limit: pageSettings.limit,
                offset: pageSettings.serverOffset,
                filters: { ByTargetType: targetType },
            },
        }),
        context.client.query<MemberBoardPinCountQuery>({
            query: MemberBoardPinCountDocument,
        }),
    ]);

    const rawPinEdges = await assertResponse(
        context,
        allPins?.data?.getMember.DetailedPins?.edges,
        allPins.error
    );

    const pagination = getPaginationOpts({
        request,
        device,
        defaultPageSize,
        totalCount:
            allPins?.data?.getMember.DetailedPins?.pageInfo.totalCount || 0,
    });

    const validPinEdges = getValidEdgeNodes<DetailedPinFragment[]>(rawPinEdges);

    return json({
        deviceType: device.type,
        directoryPageMeta,
        projectUrlSegment: projectUrlSegment || '',
        allPinsData: mapMemberDetailedPinsData(validPinEdges),
        pinCounts: mapMemberPinCountData(pinCounts?.data?.getMember),
        pagination,
    });
};

export type DesignBoardsPinsLoaderData = SerializeFrom<typeof loader>;

const DesignBoardAllPins = () => {
    const { allPinsData, pagination } = useLoaderData<typeof loader>();

    return (
        <PageLayout
            mainTitle="DESIGN BOARDS"
            subTitle={pluralize(pagination.totalCount, 'Pin')}
        >
            <AllPins allPinsData={allPinsData} pagination={pagination} />
        </PageLayout>
    );
};

export default DesignBoardAllPins;
